import React, { useState } from 'react';
import OrganizationContainer from '../../state/containers/OrganizationContainer';
import MediaContainer from '../../state/containers/MediaContainer';
import AvatarUpload from './AvatarUpload';

export interface OrganizationAvatarUploadProps {
    organizationId: string;
    organizationName: string;
    currentImageUri?: string | null;
    orgContainer: OrganizationContainer;
    mediaContainer: MediaContainer;
    size?: 'small' | 'large';
    showLabel?: boolean;
    showHint?: boolean;
    isLoading?: boolean;
    disabled?: boolean;
    onError?: (errors: string[]) => void;
    onClearError?: () => void;
    onUploadSuccess?: (imageUrl: string) => void;
}

const OrganizationAvatarUpload: React.FC<OrganizationAvatarUploadProps> = ({
    organizationId,
    organizationName,
    currentImageUri,
    orgContainer,
    mediaContainer,
    size = 'small',
    showLabel = false,
    showHint = false,
    isLoading = false,
    disabled = false,
    onError,
    onClearError,
    onUploadSuccess
}) => {
    const [customAvatarUrl, setCustomAvatarUrl] = useState<string | null>(null);
    const [isUploadingAvatar, setIsUploadingAvatar] = useState<boolean>(false);

    const handleAvatarFileChange = async (file: File) => {
        setIsUploadingAvatar(true);

        try {
            // Upload the image to get a URL
            const uploadResult = await mediaContainer.uploadOrganizationMedia(
                organizationId,
                file.name,
                file,
                '',
                () => { }
            );

            if (uploadResult.resultType === 'Ok' && uploadResult.data) {
                const imageUrl = uploadResult.data;

                // Update the organization with the new image URL
                await orgContainer.updateOrganizationInfo({
                    name: organizationName,
                    description: orgContainer.state.currentOrganization?.description,
                    imageUri: imageUrl
                });

                // Check if there were errors from the organization update
                if (orgContainer.state.errors && orgContainer.state.errors.length > 0) {
                    onError?.(orgContainer.state.errors);
                } else {
                    // Update local state with the new image URL
                    setCustomAvatarUrl(imageUrl);
                    onUploadSuccess?.(imageUrl);
                }
            } else {
                onError?.(['Failed to upload image. Please try a smaller file or different format (JPG, PNG recommended).']);
            }
        } catch (error) {
            // uploadOrganizationMedia rejects with string[] on API errors, or an Error/string on network errors
            if (Array.isArray(error) && error.length > 0) {
                onError?.(error.map(e => typeof e === 'string' ? e : String(e)));
            } else {
                const message = error?.message || (typeof error === 'string' ? error : null);
                onError?.([`Failed to upload image. ${message || 'Please try a smaller file size (under 5MB) or a different image format (JPG, PNG recommended).'}`]);
            }
        } finally {
            setIsUploadingAvatar(false);
        }
    };

    return (
        <AvatarUpload
            name={organizationName || 'ORG'}
            currentImageUri={customAvatarUrl || currentImageUri}
            size={size}
            label={showLabel ? 'Organization Logo' : undefined}
            showHint={showHint}
            isLoading={isLoading || isUploadingAvatar}
            disabled={disabled}
            onChange={handleAvatarFileChange}
            onError={onError}
            onClearError={onClearError}
        />
    );
};

export default OrganizationAvatarUpload;
